import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MfaEnrollFlow from './MfaEnrollFlow';
import MfaChallenge from './MfaChallenge';
import { mfaTokenStore } from '@/services/mfaTokenStore';
import type { MfaMethod } from '@/services/mfaService';

/**
 * Full-screen 2FA gate. Mirrors the org-selection overlay in AppShell
 * (fixed inset-0, no dismiss). Shown when the active org requires 2FA past its
 * grace window, or a required-but-unenrolled member must set up, or an enrolled
 * member is on an untrusted device. Rendered as a sibling of <Routes> in App.
 */
export default function MfaGate() {
  const {
    activeOrganizationId, mfaEnrollmentRequired, mfaMethods, mfaRecoveryAvailable,
    onMfaSatisfied, refreshMfaSession, logout,
  } = useAuth();

  // Self-correct: if the gate was raised by a racing 403 but this device is
  // actually trusted, refreshing the session mints a token and drops the gate.
  useEffect(() => { void refreshMfaSession(); }, [refreshMfaSession]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-8">
        <img src="/logo.png" alt="simcheck.ai" className="w-12 h-12 object-contain mb-5 mx-auto" />
        {mfaEnrollmentRequired ? (
          <>
            <h1 className="text-xl font-bold text-foreground text-center mb-1">Set up two-factor authentication</h1>
            <p className="text-sm text-muted-foreground text-center mb-6">Your organization requires a second factor to sign in. Choose a method to continue.</p>
            <MfaEnrollFlow
              orgId={activeOrganizationId ?? undefined}
              onEnrolled={(r) => {
                if (r.mfaToken && r.expiresAtMs) onMfaSatisfied(r.mfaToken, r.expiresAtMs);
                else void refreshMfaSession();
              }}
            />
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-foreground text-center mb-1">Two-step verification</h1>
            <p className="text-sm text-muted-foreground text-center mb-6">Confirm it's you with your second factor.</p>
            <MfaChallenge
              orgId={activeOrganizationId ?? undefined}
              methods={(mfaMethods as MfaMethod[]) || []}
              recoveryAvailable={mfaRecoveryAvailable}
              onVerified={(token, exp, sudo, sudoExp) => {
                onMfaSatisfied(token, exp);
                mfaTokenStore.setSudo(sudo, sudoExp);
              }}
            />
          </>
        )}
        <button onClick={() => void logout()} className="block w-full text-center text-sm text-muted-foreground hover:text-foreground mt-6">
          Sign out
        </button>
      </div>
    </div>
  );
}
